import { useQuery } from "@tanstack/react-query";
import {
  fetchUserBalanceStatements,
  type BalanceStatementRequest,
} from "@/features/balance/utils/fetch-student-statements";
import {
  STUDENT_STATEMENT_PAGE_SIZE,
  USER_BALANCE_STATEMENTS_STALE_TIME_MS,
  userBalanceStatementsQueryKey,
} from "@/features/balance/utils/user-balance-statements-query";
import { useAccessToken, useTenantData } from "@/hooks";

interface UseUserBalanceStatementsProps {
  userId: number;
  request: BalanceStatementRequest;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

export const useUserBalanceStatements = ({
  userId,
  request,
  page = 1,
  pageSize = STUDENT_STATEMENT_PAGE_SIZE,
  enabled = true,
}: UseUserBalanceStatementsProps) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();

  const { data, isError, isPending, isFetching, isPlaceholderData, refetch } =
    useQuery({
      queryKey: userBalanceStatementsQueryKey(userId, request, page, pageSize),
      queryFn: async () => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }
        return fetchUserBalanceStatements({
          accessToken,
          tenantId,
          userId,
          request,
          page,
          pageSize,
        });
      },
      enabled: enabled !== false && !!accessToken && userId > 0,
      placeholderData: (previousData) => previousData,
      staleTime: USER_BALANCE_STATEMENTS_STALE_TIME_MS,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  return {
    data,
    isError,
    isPending,
    isFetching,
    isPlaceholderData,
    refetch,
  };
};
